import { Promisable, ValueOf } from "type-fest";

import { throwExpression } from "@/utils/util";

/**
 * Represents a single step in the AsyncStepGraph.
 * @template TResultMap - The result map for the graph.
 * @template TName - The name of this step (must be a key of TResultMap).
 * @template TDeps - The keys of TResultMap that this step depends on.
 */
export interface AsyncStepDefinition<TResultMap, TName extends keyof TResultMap, TDeps extends keyof TResultMap> {
  deps: TDeps[];
  dependents: Set<keyof TResultMap>;
  fn: (deps: Pick<TResultMap, TDeps>) => Promisable<TResultMap[TName]>;
}

/**
 * AsyncStepGraph allows you to define asynchronous steps with dependencies and resolves them in optimal order.
 * Each step is a named async function that can depend on the results of other steps.
 * Steps are executed in parallel where possible, and only awaited when their dependencies are needed.
 *
 * @template TResultMap - An object type where each key is a step name and the value is the result type of that step.
 *
 * Example usage:
 *   const graph = new AsyncStepGraph<{foo: number, bar: string}>();
 *   graph.define('foo', [], async () => 42);
 *   graph.define('bar', ['foo'], async ({ foo }) => `Value is ${foo}`);
 *   const results = await graph.runAll();
 *   // results.foo === 42, results.bar === 'Value is 42'
 */
export class AsyncStepGraph<TResultMap extends object> {
  /**
   * Internal registry of steps and their dependencies.
   */
  private steps: {
    [K in keyof TResultMap]?: AsyncStepDefinition<TResultMap, K, keyof TResultMap>;
  } = {};

  /**
   * Tracks in-progress promises for each step to avoid duplicate execution.
   */
  private promises: { [K in keyof TResultMap]?: Promise<TResultMap[K]> } = {};

  /**
   * Define a step in the graph.
   * @param name - The unique name of the step.
   * @param deps - An array of step names this step depends on.
   * @param fn - An async function that receives a map of dependency results and returns the result for this step.
   */
  public define<K extends keyof TResultMap, TDeps extends keyof TResultMap>(
    name: K,
    deps: TDeps[],
    fn: AsyncStepDefinition<TResultMap, K, TDeps>["fn"],
  ): void {
    // Do not allow registering a step with the same name twice
    if (this.steps[name]) throw new Error(`Step '${String(name)}' is already defined`);

    // Scan the previously registered steps to determine the dependents of this step.
    const existingEntries = Object.entries(this.steps) as [keyof typeof this.steps, ValueOf<typeof this.steps>][];
    const dependents = new Set<keyof TResultMap>(
      existingEntries.filter(([_, otherDef]) => otherDef!.deps.includes(name)).map(([otherName]) => otherName),
    );

    // Register this step in the steps map
    this.steps[name] = { deps, fn, dependents };

    // Link this step as a dependent of any previously-defined dependencies
    for (const dep of deps) {
      this.steps[dep]?.dependents.add(name);
    }
  }

  /**
   * Executes all steps in the graph, resolving dependencies as needed.
   * Each step is only executed once, and dependencies are resolved recursively.
   * Steps with no dependencies are started in parallel.
   *
   * @returns A promise resolving to an object containing all step results.
   */
  public async runAll(): Promise<TResultMap> {
    // Reset the internal promises map to ensure a clean state
    this.resetPromises();

    // Start all steps in parallel (dependencies will be resolved recursively)
    const stepNames = Object.keys(this.steps) as (keyof TResultMap)[];
    const results = await Promise.all(stepNames.map((name) => this.runStep(name, new Set())));

    // Return all promise results as a single object
    return Object.fromEntries(stepNames.map((name, index) => [name, results[index]])) as TResultMap;
  }

  /**
   * Re-run a portion of the graph given one or more invalidated steps.
   *
   *  - We assume an "invalidated" step means its previously produced result (in existingResults) is no longer valid.
   *  - All steps that (transitively) depend on any invalidated step must also be recomputed so their results remain
   *    consistent.
   *  - Upstream dependency steps of an invalidated step are ONLY recomputed if they themselves are listed as
   *    invalidated; otherwise their existing results (or provided precomputed results) are treated as still valid.
   *  - You may provide `precomputedResults` to short‑circuit recomputation for any step (invalidated or downstream). This
   *    is useful when data has already been updated in-place elsewhere and want the graph updated for any dependencies.
   *  - `existingResults` is not mutated; a new object is returned containing the merged, updated results for all defined
   *    steps.
   *
   * @param existingResults Previously computed results (e.g. from runAll or a prior incremental run).
   * @param invalidatedStepNames Steps whose prior results are invalid. Must be keys of defined steps.
   * @param precomputedResults Optional map of step results to use instead of (re)computing.
   * @returns A new complete results object for the graph (same shape as TResultMap).
   */
  public async runIncremental(
    existingResults: Readonly<Partial<TResultMap>>,
    invalidatedStepNames: (keyof TResultMap)[],
    precomputedResults?: Readonly<Partial<TResultMap>>,
  ): Promise<TResultMap> {
    // Compute full set of steps needing recompute: invalidated + all downstream dependents
    const allInvalidatedSteps = [
      ...invalidatedStepNames,
      ...(Object.keys(precomputedResults ?? {}) as (keyof TResultMap)[]),
    ];
    const toRecompute = this.getDependentsForSteps(allInvalidatedSteps);

    // Initialize the internal `promises` map with all existing results that do not need to be recomputed, and all the
    // precomputed results that should be used instead of recomputing
    const existingEntries = Object.entries(existingResults) as [keyof TResultMap, TResultMap[keyof TResultMap]][];
    const precomputedEntries = Object.entries(precomputedResults ?? {}) as [
      keyof TResultMap,
      TResultMap[keyof TResultMap],
    ][];
    const unchangedResults = Object.fromEntries([
      ...existingEntries.filter(([stepName]) => !toRecompute.has(stepName)),
      ...precomputedEntries,
    ]) as Partial<TResultMap>;
    this.resetPromises(unchangedResults);

    // Start all remaining steps to recompute in parallel (dependencies will be resolved recursively)
    const resultsArray = await Promise.all([...toRecompute].map((name) => this.runStep(name, new Set())));
    const resultsMap = Object.fromEntries([...toRecompute].map((name, index) => [name, resultsArray[index]]));

    // Combine the recomputed results with the existing unchanged results
    return { ...unchangedResults, ...resultsMap } as TResultMap;
  }

  private resetPromises(results: Partial<TResultMap> = {}): void {
    this.promises = Object.fromEntries(
      Object.entries(results).map(([stepName, result]) => [stepName, Promise.resolve(result)]),
    ) as { [K in keyof TResultMap]?: Promise<TResultMap[K]> };
  }

  private async runStep<TName extends keyof TResultMap>(
    name: TName,
    visitedSteps: Set<TName>,
  ): Promise<TResultMap[TName]> {
    // Fail if this step has already been visited in this execution path
    if (visitedSteps.has(name)) {
      throw new Error(`Cyclical dependency detected at step: ${String(name)}`);
    }

    // Add this step to the list of visited nodes
    const newVisitedSteps = new Set(visitedSteps);
    newVisitedSteps.add(name);

    // Return existing promise if this step is already running
    if (this.promises[name]) return await this.promises[name];

    // Otherwise, register a new promise for this step and return the result
    return await (this.promises[name] = this.runStepInternal(name, newVisitedSteps));
  }

  private async runStepInternal<TName extends keyof TResultMap>(
    name: TName,
    visitedSteps: Set<TName>,
  ): Promise<TResultMap[TName]> {
    // Get the step definition
    const { deps, fn } = this.steps[name] ?? throwExpression(`Step '${name.toString()}' is not defined`);

    // Resolve all dependencies first, passing along the already visited steps
    const depResults = await Promise.all(deps.map((dep) => this.runStep(dep, visitedSteps)));
    const depResultsMap = Object.fromEntries(deps.map((dep, index) => [dep, depResults[index]])) as TResultMap;

    // Execute the step function with resolved dependencies
    const consoleLabel = `Graph step '${name.toString()}'`;
    console.time(consoleLabel);
    try {
      return await Promise.resolve(fn(depResultsMap));
    } finally {
      console.timeEnd(consoleLabel);
    }
  }

  /**
   * Get all dependents (recursive) for the provided step names. The returned set also includes the provided step names.
   */
  private getDependentsForSteps(names: (keyof TResultMap)[]): Set<keyof TResultMap> {
    const visited = new Set<keyof TResultMap>();
    names.forEach((name) => this.getDependentsForStep(name, visited));
    return visited;
  }

  /**
   * Get all dependents (recursive) for the provided step name. The returned set also includes the provided step name
   */
  private getDependentsForStep(name: keyof TResultMap, visited = new Set<keyof TResultMap>()): Set<keyof TResultMap> {
    visited.add(name);
    this.steps[name]!.dependents.forEach((name) => this.getDependentsForStep(name, visited));
    return visited;
  }
}
